import {I18n} from '@aws-amplify/core';
import {DataTableColumn} from 'mantine-datatable';

import {formatTimestamp} from '../../../utils/TableTimestamps';
import {Card} from '../types';

// FNP-918: was `tables/columns.tsx` — a bare array where EVERY column was
// `render`-only, with no `accessor` on any of them.
//
// That is the distinction that made this module unsortable. `render` controls
// how a cell LOOKS; `accessor` is what sorting sorts BY. A render-only config
// looks complete in review and makes sorting structurally impossible — you
// cannot add `sortable: true` to a column that has nothing to sort on. Moved
// into a hook to match the platform shape (checklist §3z).
//
// The old config also rendered the id cell as a <span> with an inline colour
// and an onClick that opened an edit drawer. That is a hand-rolled link: not
// keyboard-reachable, no focus ring, and it hardcoded a colour rather than
// taking a theme token (§2, §13). Row click now navigates (RecordsTable owns
// `onRowClick`), so the cell is plain text.
export const useCardsTableColumns = () => {

    const columns: DataTableColumn<Card>[] = [
        {
            accessor: 'id',
            sortable: true,
            title: I18n.get('Card'),
            width: '1fr',
            // Long ids in monospace (§13). The id is what an operator pastes in
            // from the Stripe dashboard, so it stays visible in full.
            render: (item: Card) => (
                <span style={{fontFamily: 'monospace'}}>{item.id}</span>
            ),
        },
        {
            accessor: 'brand',
            sortable: true,
            title: I18n.get('Brand'),
            width: '1fr',
            // Nullable fields render the em-dash placeholder, never blank or
            // `undefined` (§13). Nulls sort LAST in both directions — that is
            // sortRecords' rule, not something this column configures.
            render: (item: Card) => item.brand ?? '—',
        },
        {
            accessor: 'last4',
            sortable: true,
            title: I18n.get('Last 4'),
            width: '1fr',
            // Displayed as the masked form an operator recognises. Only the
            // last four digits are ever fetched — the PAN is never in the
            // response, so there is nothing here to redact (§11).
            render: (item: Card) => item.last4 ? `•••• ${item.last4}` : '—',
        },
        {
            // Sorts on the normalised YYYYMM value rather than the displayed
            // "M/YYYY" string: sorting the display text would put 10/2026
            // before 9/2026, and 1/2031 before 9/2026. See useCards.ts.
            accessor: 'expiresAtSortKey',
            sortable: true,
            title: I18n.get('Expiration'),
            width: '1fr',
            render: (item: Card) =>
                (item.expMonth && item.expYear)
                    ? `${item.expMonth}/${item.expYear}`
                    : '—',
        },
        {
            accessor: 'country',
            sortable: true,
            title: I18n.get('Country'),
            width: '1fr',
            render: (item: Card) => item.country ?? '—',
        },
        {
            accessor: 'created',
            sortable: true,
            title: I18n.get('Created'),
            width: '1fr',
            // formatTimestamp guards null/unparseable and renders the em-dash;
            // an unguarded moment(undefined) would render RIGHT NOW as fact.
            render: (item: Card) => formatTimestamp(item.created),
        },
    ];

    return {columns};
};
